import {
  faArrowTrendDown,
  faArrowTrendUp,
  faMinus,
} from '@fortawesome/pro-solid-svg-icons';
import { cva } from 'class-variance-authority';
import { ReactNode, useState } from 'react';

import {
  Action,
  Alert,
  AmountLineItem,
  Card,
  cn,
  Icon,
  LineItemList,
  Modal,
  OrderLineItem,
} from 'corso-admin-ui';
import { match } from 'ts-pattern';
import {
  useClaim,
  useIsLocked,
} from '~/features/claimReview/ClaimReviewProvider';
import { useClaimLedgerSummary } from '~/features/claimReview/hooks/useClaimLedgerSummary';
import { useClaimOrderLineage } from '~/features/claimReview/hooks/useClaimOrderLineage';
import { useCurrency } from '~/hooks/useCurrency';
import { formatter } from 'corso-web-lib';
import { ClaimLineageHistory } from './ClaimOrderLineage';
import { GiftCardLineItem } from '~/components/lineItems/GiftCardLineItem';
import { RefundLineItem } from '~/components/lineItems/RefundLineItem';

type Ledger = NonNullable<ReturnType<typeof useClaimLedgerSummary>['data']>;
type PricedLine = Ledger['credits']['lineItems'][number];

// the ledger only carries ids; names, images and skus come from the claim
function useLineItemLookup() {
  const { claimLineItems } = useClaim();
  return (id: PricedLine['id']) =>
    claimLineItems.find((cli) => `${cli.id}` === `${id}`);
}

function useToLineItems() {
  const lookup = useLineItemLookup();
  const { currencyCode } = useCurrency();

  return (lines: PricedLine[]) =>
    lines.map((line, index) => {
      const cli = lookup(line.id);
      // an exchanged item is priced against the returned line but is a different product; show that one
      const exchanged = line.idFromPlatform
        ? [cli?.variantExchangeLineItem, ...(cli?.replacementItems ?? [])].find(
            (item) => item?.idFromPlatform === line.idFromPlatform,
          )
        : undefined;
      const original = cli?.originalStoreOrderLineItem;

      return {
        // replacement items all carry their claim line item's id, so the index keeps them distinct
        id: `${line.id}-${index}`,
        title: exchanged
          ? (exchanged.title ?? `Variant ${exchanged.idFromPlatform}`)
          : (original?.name ?? `Line item ${line.id}`),
        src: exchanged ? exchanged.imgUrl : original?.imgUrl,
        price: {
          currencyCode,
          unitPrice: line.unitPrice,
          unitTax: line.unitTax,
        },
        quantity: line.quantity,
        sku: exchanged ? exchanged.sku : original?.sku,
        options: exchanged ? exchanged.options : original?.optionsFromPlatform,
      };
    });
}

const toSections = (sections: Ledger['credits']['sections']) =>
  sections.map((section) => ({
    id: `${section.type}-${section.label}`,
    title: section.label,
    amount: section.amount,
  }));

type BenefitTrend = 'up' | 'down' | 'neutral';

const getBenefitTrend = (amount: number): BenefitTrend =>
  match(amount)
    .when(
      (a) => a > 0,
      () => 'up' as const,
    )
    .when(
      (a) => a < 0,
      () => 'down' as const,
    )
    .otherwise(() => 'neutral' as const);

const benefitColorVariants = cva('font-bold tabular-nums', {
  variants: {
    trend: {
      up: 'text-emerald-600',
      down: 'text-red-600',
      neutral: 'text-neutral-500',
    },
    size: {
      sm: 'text-sm',
      lg: 'text-xl',
    },
  },

  defaultVariants: {
    trend: 'neutral',
    size: 'sm',
  },
});

function TrendIcon({ trend }: { trend: BenefitTrend }) {
  return match(trend)
    .with('up', () => (
      <Icon
        icon={faArrowTrendUp}
        className="size-5 text-emerald-600"
        aria-label="Positive benefit"
      />
    ))
    .with('down', () => (
      <Icon
        icon={faArrowTrendDown}
        className="size-5 text-red-600"
        aria-label="Negative benefit"
      />
    ))
    .with('neutral', () => (
      <Icon
        icon={faMinus}
        className="size-5 text-neutral-500"
        aria-label="Neutral benefit"
      />
    ))
    .exhaustive();
}

export function BenefitMetric({
  label,
  amount,
  currencyCode,
}: {
  label: string;
  amount: number;
  currencyCode: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <TrendIcon trend={getBenefitTrend(amount)} />
        <span className="text-sm text-neutral-600">{label}</span>
      </div>
      <div className={benefitColorVariants({ trend: getBenefitTrend(amount) })}>
        {formatter.currency(amount, currencyCode)}
      </div>
    </div>
  );
}

function ClaimBenefitScore({
  ledger,
  currencyCode,
}: {
  ledger: Ledger;
  currencyCode: string;
}) {
  return (
    <>
      {ledger.benefit.newRevenue !== 0 && (
        <BenefitMetric
          label="New Revenue"
          amount={ledger.benefit.newRevenue}
          currencyCode={currencyCode}
        />
      )}
      {ledger.benefit.retainedRevenue !== 0 && (
        <BenefitMetric
          label="Retained Revenue"
          amount={ledger.benefit.retainedRevenue}
          currencyCode={currencyCode}
        />
      )}
    </>
  );
}

function SummaryGroup({
  heading,
  defaultOpen = false,
  amount,
  description,
  lineItems,
  sections,
}: {
  heading: string;
  description: string;
  amount: number;
  defaultOpen?: boolean;
  lineItems: ReturnType<ReturnType<typeof useToLineItems>>;
  sections: ReturnType<typeof toSections>;
}) {
  const { currencyCode } = useCurrency();
  return (
    <LineItemList
      defaultOpen={defaultOpen}
      summary={{ title: heading, description, currencyCode, amount }}
    >
      {!lineItems.length && !sections.length && (
        <LineItemList.Item>
          <p className="text-sm text-neutral-500">
            No additional information available.
          </p>
        </LineItemList.Item>
      )}
      {lineItems.map(({ id, ...lineItem }) => (
        <LineItemList.Item key={id}>
          {/* oxlint-disable-next-line react/jsx-props-no-spreading */}
          <OrderLineItem size="sm" {...lineItem} />
        </LineItemList.Item>
      ))}
      {sections.map(({ id, ...section }) => (
        <LineItemList.Item key={id}>
          <AmountLineItem
            size="sm"
            // oxlint-disable-next-line react/jsx-props-no-spreading
            {...section}
            currencyCode={currencyCode}
          />
        </LineItemList.Item>
      ))}
    </LineItemList>
  );
}

function BenefitCard({
  label,
  amount,
  description,
  currencyCode,
  variant = 'default',
}: {
  label: string;
  amount: number;
  description: ReactNode;
  currencyCode: string;
  variant?: 'default' | 'featured';
}) {
  const isFeatured = variant === 'featured';
  return (
    <div
      className={cn(
        'rounded-lg border-2 border-neutral-200 bg-white p-4',
        isFeatured && 'mb-3',
      )}
    >
      <div className={cn(isFeatured && 'flex items-center justify-between')}>
        <div
          className={cn(
            'flex items-center gap-2',
            isFeatured && 'gap-3',
            !isFeatured && 'mb-2',
          )}
        >
          <TrendIcon trend={getBenefitTrend(amount)} />
          <div>
            <span className="text-sm font-medium text-neutral-700">
              {label}
            </span>
            {isFeatured && (
              <p className="text-xs text-neutral-600">{description}</p>
            )}
          </div>
        </div>
        <div
          className={benefitColorVariants({
            trend: getBenefitTrend(amount),
            size: isFeatured ? 'lg' : 'sm',
          })}
        >
          {formatter.currency(amount, currencyCode)}
        </div>
      </div>
      {!isFeatured && (
        <p className="mt-1 text-xs text-neutral-600">{description}</p>
      )}
    </div>
  );
}

function ClaimLedgerOverviewChart({
  ledger,
  currencyCode,
}: {
  ledger: Ledger;
  currencyCode: string;
}) {
  const totalBenefit =
    ledger.benefit.newRevenue + ledger.benefit.retainedRevenue;

  return (
    <>
      <BenefitCard
        label="Total Benefit"
        amount={totalBenefit}
        description="Combined revenue impact"
        currencyCode={currencyCode}
        variant="featured"
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <BenefitCard
          label="New Revenue"
          amount={ledger.benefit.newRevenue}
          description="From upsells, fees, and shop now orders."
          currencyCode={currencyCode}
        />
        <BenefitCard
          label="Retained Revenue"
          amount={ledger.benefit.retainedRevenue}
          description={
            ledger.benefit.retainedRevenue < 0
              ? 'Refunded to customer'
              : 'Kept as store credit or exchanged for a new item.'
          }
          currencyCode={currencyCode}
        />
      </div>
    </>
  );
}

function CustomerReceives({ ledger }: { ledger: Ledger }) {
  const toLineItems = useToLineItems();
  const { deductions, credits, totals, lines } = ledger;

  const newItems = toLineItems(deductions.lineItems);

  const incentiveAmount = credits.sections
    .filter((s) => s.type === 'INCENTIVE')
    .reduce((sum, s) => sum + s.amount, 0);

  const externallyResolved = lines.some(
    (line) => line.kind === 'credit' && line.resolvedExternally,
  );

  const receivesNothing =
    !newItems.length && totals.refund <= 0 && totals.giftCard <= 0;

  return (
    <div className="border-t border-neutral-200 pt-4">
      <div className="flex flex-col gap-3 rounded-lg bg-neutral-50 p-4">
        <div>
          <Card.Heading>Customer Receives</Card.Heading>
          <p className="text-xs text-neutral-600">
            The new items and money going back to the customer.
          </p>
        </div>

        {receivesNothing && (
          <p className="text-sm text-neutral-500">Nothing issued.</p>
        )}

        {newItems.map(({ id, ...item }) => (
          // oxlint-disable-next-line react/jsx-props-no-spreading
          <OrderLineItem key={id} size="sm" {...item} />
        ))}

        {totals.refund > 0 && (
          <RefundLineItem
            refund={{
              id: 'refund',
              amount: totals.refund,
              wasResolvedExternally: externallyResolved,
            }}
          />
        )}

        {totals.giftCard > 0 && (
          <GiftCardLineItem
            giftCard={{
              id: 'giftCard',
              amount: totals.giftCard,
              incentiveAmount,
              wasResolvedExternally: externallyResolved,
            }}
          />
        )}
      </div>
    </div>
  );
}

export function ClaimLedger({ ledger }: { ledger: Ledger }) {
  const { currencyCode } = useCurrency();
  const isLocked = useIsLocked();
  const toLineItems = useToLineItems();
  const { credits, deductions, balance, payment, unallocatedDeduction } =
    ledger;

  const money = (amount: number) => formatter.currency(amount, currencyCode);

  return (
    <>
      <SummaryGroup
        defaultOpen
        heading="Credits"
        description="Returning items, incentives and payments."
        amount={credits.total}
        lineItems={toLineItems(credits.lineItems)}
        sections={toSections(credits.sections)}
      />

      <SummaryGroup
        defaultOpen
        heading="Deductions"
        description="New items, fees and shipping."
        amount={deductions.total}
        lineItems={toLineItems(deductions.lineItems)}
        sections={toSections(deductions.sections)}
      />

      {payment.waivedAmount > 0 && (
        <Alert
          variant="info"
          heading="Return Adjustment"
        >{`${money(payment.waivedAmount)} owed by the customer is under Stripe's minimum and is credited back instead of collected.`}</Alert>
      )}

      <div className="flex items-center justify-between gap-1">
        <Card.Heading>Balance</Card.Heading>
        <span className="pr-3 text-sm font-medium tabular-nums">
          {money(balance)}
        </span>
      </div>

      {balance > 0 && (
        <Alert
          heading="Uncollectable Balance"
          variant="warning"
        >{`${money(balance)} will be left uncollected. Collecting additional payment from the customer is not supported today`}</Alert>
      )}

      {balance <= 0 && unallocatedDeduction > 0 && (
        <Alert variant="warning" heading="Uncollected Deductions">
          {`${money(unallocatedDeduction)} of deductions has no credit line to come out of and will be left uncollected.`}
        </Alert>
      )}

      {!isLocked && <CustomerReceives ledger={ledger} />}
    </>
  );
}

function ClaimLedgerSummaryModal({
  show,
  onClose,
  ledger,
}: {
  show: boolean;
  onClose: () => void;
  ledger: Ledger;
}) {
  const { currencyCode } = useCurrency();

  return (
    <Modal title="" show={show} onClose={onClose} size="lg">
      <ClaimLedgerOverviewChart ledger={ledger} currencyCode={currencyCode} />
      <ClaimLedger ledger={ledger} />
    </Modal>
  );
}

function LoadingBar() {
  return (
    <div className="relative flex h-2 w-full overflow-hidden rounded-full bg-neutral-200">
      <div className="absolute inset-0 animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-neutral-200 via-neutral-300 to-neutral-200" />
    </div>
  );
}

/** The chain of orders and claims this one came out of, for an exchange of an exchange. */
function ClaimLineage() {
  const [show, setShow] = useState(false);
  const { originalStoreOrder } = useClaim();
  const { data: lineage, isPending } = useClaimOrderLineage();

  // ? maybe just show lineage all the time?
  if (!originalStoreOrder.isExchangeOfExchange) return null;

  return (
    <>
      <Card.Line>
        <Card.Heading>Claim lineage</Card.Heading>
        <Action
          onClick={() => setShow(true)}
          icon="branch"
          accessibilityLabel="View Claim Lineage"
          variant="ghost"
          disabled={isPending || !lineage}
        />
      </Card.Line>
      {isPending && <LoadingBar />}
      {lineage && (
        <Modal
          title="Claim lineage"
          show={show}
          onClose={() => setShow(false)}
          size="lg"
        >
          <ClaimLineageHistory lineage={lineage} />
        </Modal>
      )}
    </>
  );
}

export function ClaimLedgerCard() {
  const [show, setShow] = useState(false);
  const { claimType } = useClaim();
  const { data: ledger, isPending } = useClaimLedgerSummary();
  const { currencyCode } = useCurrency();

  if (claimType !== 'Return') return null; // only shown for returns

  return (
    <Card>
      <Card.Line>
        <Card.Heading>Claim ledger</Card.Heading>
        <Action
          onClick={() => setShow(true)}
          icon="dollar"
          accessibilityLabel="View Ledger"
          variant="ghost"
          disabled={isPending || !ledger}
        />
      </Card.Line>
      {isPending ? (
        <LoadingBar />
      ) : (
        ledger && (
          <>
            <ClaimBenefitScore ledger={ledger} currencyCode={currencyCode} />
            <ClaimLedgerSummaryModal
              show={show}
              onClose={() => setShow(false)}
              ledger={ledger}
            />
          </>
        )
      )}
      <ClaimLineage />
    </Card>
  );
}
